import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Monitor,
    LayoutGrid,
    Users,
    DollarSign,
    BarChart3,
    Download,
    Plus,
    ChevronDown,
    MoreVertical,
    FileBarChart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import api from '../../utils/api';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import BusinessSystemsDashboard from './BusinessSystemsDashboard';
import SystemByUser from './SystemByUser';
import CostAnalysis from './CostAnalysis';
import LicenseUtilization from './LicenseUtilization';

const BusinessSystemsManagement: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [systems, setSystems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);

    const fetchSystems = async () => {
        try {
            setLoading(true);
            const data = await api.get('/api/business-systems');
            setSystems(data);
        } catch (err) {
            console.error('Error fetching systems:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSystems();
    }, []);

    const tabs = [
        { id: 'dashboard', label: 'All Systems', icon: <LayoutGrid size={18} />, action: ACTIONS.READ, subject: RESOURCES.BUSINESS_SYSTEM },
        { id: 'by-user', label: 'System by User', icon: <Users size={18} />, action: ACTIONS.READ, subject: RESOURCES.BUSINESS_SYSTEM },
        { id: 'cost', label: 'Cost Analysis', icon: <DollarSign size={18} />, action: ACTIONS.READ, subject: RESOURCES.BUSINESS_SYSTEM },
        { id: 'license', label: 'License Utilization', icon: <BarChart3 size={18} />, action: ACTIONS.READ, subject: RESOURCES.BUSINESS_SYSTEM },
    ].filter(tab => ability.can(tab.action, tab.subject));

    const escapeCSV = (value: any) => {
        const str = value == null ? '' : String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const handleExportCSV = () => {
        let csvContent = '';
        let filename = 'business-systems.csv';

        if (activeTab === 'dashboard') {
            const activeSystems = systems.filter(s => s.status !== 'Archived');
            const archivedSystems = systems.filter(s => s.status === 'Archived');
            const displayedSystems = showArchived ? archivedSystems : activeSystems;

            const headers = [
                'Name', 'Category', 'Department', 'Status', 'Purchase Mode', 'Monthly Cost',
                'Payment Method', 'Total Licenses', 'Used Licenses', '2FA', 'Vendor',
                'Website', 'Support Email', 'Contact Name', 'Contact Phone',
                'Contract Number', 'Renewal Date', 'Notes'
            ];

            const rows = displayedSystems.map(system => [
                system.name,
                system.category,
                system.department,
                system.status,
                system.purchaseMode,
                system.monthlyCost,
                system.paymentMethod,
                system.totalLicenses,
                system.usedLicenses,
                system.twoFactorAuth,
                system.vendor,
                system.website,
                system.supportEmail,
                system.contactName,
                system.contactPhone,
                system.contractNumber,
                system.renewalDate,
                system.notes,
            ].map(escapeCSV));

            csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            filename = showArchived ? 'archived-business-systems.csv' : 'business-systems.csv';
        } else if (activeTab === 'by-user') {
            const headers = ['User Name', 'Email', 'Department', 'Systems Count', 'Est. Monthly Cost', 'Primary Systems'];
            const userMap = new Map<number, any>();

            systems.forEach(system => {
                if (!system.assignedUsers) return;
                const costPerLicense = system.totalLicenses > 0
                    ? (parseFloat(system.monthlyCost) / system.totalLicenses)
                    : 0;
                system.assignedUsers.forEach((assignment: any) => {
                    const user = assignment.user;
                    if (!user) return;
                    if (!userMap.has(user.id)) {
                        userMap.set(user.id, {
                            name: user.name,
                            email: user.email,
                            department: user.department?.departmentName || 'Unassigned',
                            systemsCount: 0,
                            estMonthlyCost: 0,
                            primarySystems: []
                        });
                    }
                    const userData = userMap.get(user.id);
                    userData.systemsCount += 1;
                    userData.estMonthlyCost += costPerLicense;
                    userData.primarySystems.push(system.name);
                });
            });

            const rows = Array.from(userMap.values()).map((user: any) => [
                user.name,
                user.email,
                user.department,
                user.systemsCount,
                user.estMonthlyCost.toFixed(2),
                user.primarySystems.join('; '),
            ].map(escapeCSV));

            csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            filename = 'business-systems-by-user.csv';
        } else if (activeTab === 'cost') {
            const headers = ['Name', 'Category', 'Status', 'Monthly Cost', 'Total Licenses', 'Cost Per License'];
            const rows = systems.map(system => {
                const costPerLicense = system.totalLicenses > 0
                    ? (parseFloat(system.monthlyCost || 0) / system.totalLicenses).toFixed(2)
                    : '0.00';
                return [
                    system.name,
                    system.category,
                    system.status,
                    parseFloat(system.monthlyCost || 0).toFixed(2),
                    system.totalLicenses,
                    costPerLicense,
                ].map(escapeCSV);
            });
            csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            filename = 'business-systems-cost-analysis.csv';
        } else if (activeTab === 'license') {
            const headers = ['Name', 'Category', 'Status', 'Total Licenses', 'Used Licenses', 'License Utilization %', 'Monthly Cost', 'Cost Per License', 'Potential Savings'];
            const rows = systems.map(system => {
                const monthlyCost = parseFloat(system.monthlyCost || 0);
                const costPerLicense = system.totalLicenses > 0
                    ? (monthlyCost / system.totalLicenses)
                    : 0;
                const unusedLicenses = Math.max(0, (system.totalLicenses || 0) - (system.usedLicenses || 0));
                const potentialSavings = costPerLicense * unusedLicenses;
                const utilization = system.totalLicenses > 0
                    ? ((system.usedLicenses / system.totalLicenses) * 100).toFixed(1)
                    : '0.0';
                return [
                    system.name,
                    system.category,
                    system.status,
                    system.totalLicenses,
                    system.usedLicenses,
                    utilization,
                    monthlyCost.toFixed(2),
                    costPerLicense.toFixed(2),
                    potentialSavings.toFixed(2),
                ].map(escapeCSV);
            });
            csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            filename = 'business-systems-license-utilization.csv';
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="flex flex-col space-y-6 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Business Systems</h1>
                    <p className="text-gray-500 font-medium">
                        Manage business systems, track costs, licenses, and user access
                    </p>
                </div>
                {/* Desktop Actions */}
                <div className="hidden md:flex items-center gap-3">
                    <Button
                        variant="outline"
                        className="flex items-center gap-2 text-sm font-bold text-gray-700 border-gray-100 hover:bg-gray-50"
                        onClick={handleExportCSV}
                    >
                        <Download size={16} />
                        Export CSV
                    </Button>
                    <Button
                        className="flex items-center gap-2 bg-slate-900 text-white border-0 shadow-sm text-sm font-bold"
                        onClick={() => navigate('/business-systems/new')}
                    >
                        <Plus size={16} />
                        Add System
                    </Button>
                </div>

                {/* Mobile Actions Dropdown */}
                <div className="md:hidden">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="flex items-center gap-2 w-full justify-center  border-gray-100">
                                <MoreVertical size={18} />
                                Actions
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56  border-gray-100">
                            <DropdownMenuItem className="py-3 font-medium" onClick={handleExportCSV}>
                                <Download size={18} className="mr-2" />
                                <span>Export CSV</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem className="py-3 bg-slate-900 text-white m-1 hover:bg-slate-800 focus:bg-slate-800 focus:text-white rounded-lg" onClick={() => navigate('/business-systems/new')}>
                                <Plus size={18} className="mr-2" />
                                <span>Add System</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            {/* Tab Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                {/* Desktop Tab List */}
                <div className="hidden md:block w-full overflow-x-auto pb-1 no-scrollbar">
                    <TabsList className="bg-gray-100/80 p-1.5 rounded-2xl min-w-max md:w-fit flex items-center h-auto border border-gray-100/50">
                        {tabs.map((tab) => (
                            <TabsTrigger
                                key={tab.id}
                                value={tab.id}
                                className=" px-4 md:px-6 py-2.5 flex items-center gap-2.5 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm font-bold transition-all whitespace-nowrap text-gray-500"
                            >
                                <span className={activeTab === tab.id ? "text-slate-900" : "text-gray-400"}>
                                    {tab.icon}
                                </span>
                                <span className="text-sm">{tab.label}</span>
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </div>

                {/* Mobile Tab Dropdown */}
                <div className="md:hidden w-full">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-gray-50 border-gray-100  h-12 px-4 shadow-sm">
                                <div className="flex items-center gap-2">
                                    {tabs.find(t => t.id === activeTab)?.icon}
                                    <span className="font-bold text-slate-900">
                                        {tabs.find(t => t.id === activeTab)?.label || 'Menu'}
                                    </span>
                                </div>
                                <ChevronDown size={18} className="text-gray-400" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[calc(100vw-3rem)]  border-gray-100">
                            {tabs.map((tab) => (
                                <DropdownMenuItem
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-3.5 flex items-center gap-3 font-bold ${activeTab === tab.id ? "bg-slate-50 text-slate-900 focus:bg-slate-50 focus:text-slate-900" : "text-gray-500"
                                        }`}
                                >
                                    <div className={`transition-colors ${activeTab === tab.id ? "text-slate-900" : "text-gray-400"
                                        }`}>
                                        {tab.icon}
                                    </div>
                                    <span>{tab.label}</span>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Content Area */}
                <div className="pt-6">
                    <TabsContent value="dashboard">
                        <BusinessSystemsDashboard systems={systems} loading={loading} refresh={fetchSystems} showArchived={showArchived} setShowArchived={setShowArchived} />
                    </TabsContent>
                    <TabsContent value="by-user">
                        <SystemByUser systems={systems} loading={loading} />
                    </TabsContent>
                    <TabsContent value="cost">
                        <CostAnalysis systems={systems} loading={loading} />
                    </TabsContent>
                    <TabsContent value="license">
                        <LicenseUtilization systems={systems} loading={loading} />
                    </TabsContent>
                </div>
            </Tabs>
        </div>
    );
};

export default BusinessSystemsManagement;
